import React from 'react';

/**
 * Elements whose children must be elements, never text.
 */
const NO_TEXT_CHILDREN = new Set([
  'br',
  'col',
  'colgroup',
  'dl',
  'hr',
  'iframe',
  'img',
  'input',
  'link',
  'menuitem',
  'meta',
  'ol',
  'param',
  'select',
  'table',
  'tbody',
  'tfoot',
  'thead',
  'tr',
  'ul',
  'wbr',
]);

/**
 * Boolean HTML attributes. The DOM reports them as an empty string, which React
 * would treat as false and drop, so they are passed on as `true`.
 */
const BOOLEAN_ATTRIBUTES = new Set([
  'allowfullscreen',
  'async',
  'autofocus',
  'autoplay',
  'checked',
  'controls',
  'default',
  'defer',
  'disabled',
  'hidden',
  'itemscope',
  'loop',
  'multiple',
  'muted',
  'nomodule',
  'novalidate',
  'open',
  'playsinline',
  'readonly',
  'required',
  'reversed',
  'selected',
]);

/**
 * HTML attributes (lowercase in the DOM) whose React prop is spelled
 * differently. Everything else is passed on as is, with hyphenated SVG
 * attributes such as `stroke-width` converted to camelCase.
 */
const ATTRIBUTE_NAMES: Record<string, string> = {
  class: 'className',
  for: 'htmlFor',
  accesskey: 'accessKey',
  allowfullscreen: 'allowFullScreen',
  autocapitalize: 'autoCapitalize',
  autocomplete: 'autoComplete',
  autofocus: 'autoFocus',
  autoplay: 'autoPlay',
  cellpadding: 'cellPadding',
  cellspacing: 'cellSpacing',
  charset: 'charSet',
  colspan: 'colSpan',
  contenteditable: 'contentEditable',
  controlslist: 'controlsList',
  crossorigin: 'crossOrigin',
  datetime: 'dateTime',
  enctype: 'encType',
  formaction: 'formAction',
  frameborder: 'frameBorder',
  hreflang: 'hrefLang',
  inputmode: 'inputMode',
  maxlength: 'maxLength',
  minlength: 'minLength',
  nomodule: 'noModule',
  novalidate: 'noValidate',
  playsinline: 'playsInline',
  radiogroup: 'radioGroup',
  readonly: 'readOnly',
  referrerpolicy: 'referrerPolicy',
  rowspan: 'rowSpan',
  spellcheck: 'spellCheck',
  srcdoc: 'srcDoc',
  srclang: 'srcLang',
  srcset: 'srcSet',
  tabindex: 'tabIndex',
  usemap: 'useMap',
  viewbox: 'viewBox',
  'accept-charset': 'acceptCharset',
  'http-equiv': 'httpEquiv',
  'xlink:href': 'xlinkHref',
  'xml:lang': 'xmlLang',
};

const camelCase = (str: string) =>
  str.replace(/-([a-z])/g, (s, char: string) => char.toUpperCase());

function toPropName(name: string) {
  if (ATTRIBUTE_NAMES[name]) return ATTRIBUTE_NAMES[name];
  if (/^(data|aria)-/.test(name)) return name;
  return name.includes('-') ? camelCase(name) : name;
}

/**
 * Convert an inline style declaration into a React style object.
 *
 * @param {CSSStyleDeclaration} style
 *
 * @return {object}
 */
function toStyleObject(style: CSSStyleDeclaration) {
  const result: Record<string, string> = {};
  for (let i = 0; i < style.length; i++) {
    const property = style[i];
    const key = property.startsWith('--') ? property : camelCase(property);
    result[key] = style.getPropertyValue(property);
  }
  return result;
}

function toProps(node: Element, key: string) {
  const props: Record<string, unknown> = { key };

  Array.from(node.attributes).forEach(({ name, value }) => {
    if (name === 'style') {
      props.style = toStyleObject((node as HTMLElement).style);
    } else if (BOOLEAN_ATTRIBUTES.has(name)) {
      props[toPropName(name)] = true;
    } else {
      props[toPropName(name)] = value;
    }
  });

  return props;
}

function convertNode(
  node: Node,
  level: number,
  index: number
): React.ReactNode {
  const key = `${level}-${index}`;

  switch (node.nodeType) {
    case 1: // element
      return React.createElement(
        node.nodeName.toLowerCase(),
        toProps(node as Element, key),
        convertNodes(node.childNodes, level + 1)
      );
    case 3: {
      // text
      const parent = node.parentNode;
      if (parent && NO_TEXT_CHILDREN.has(parent.nodeName.toLowerCase())) {
        return null;
      }
      return node.nodeValue;
    }
    default:
      // comments and everything else
      return null;
  }
}

function convertNodes(
  nodeList: NodeListOf<ChildNode> | Node[],
  level: number
): React.ReactNode[] | null {
  const children = Array.from<Node>(nodeList)
    .map((node, index) => convertNode(node, level, index))
    .filter(Boolean);

  return children.length ? children : null;
}

/**
 * Convert DOM nodes to React elements.
 *
 * Used to hand server rendered markup (the children of a custom element) to a
 * React component. Comments are dropped, and text inside elements that can't
 * contain text (`table`, `ul`, ...) is ignored.
 *
 * @param {NodeList|Node[]} nodes
 *
 * @return {Array|null}
 */
export default function domToReact(nodes: NodeListOf<ChildNode> | Node[]) {
  return convertNodes(nodes, 0);
}
